import { useState, useEffect } from 'react';
import { getCriptoInfo } from '../../services/AgentService';
import { formatFiatAmount } from '../../components/SelectFiat';

function WalletCard(props) {
    const [imageUrl, setImageUrl] = useState(null);
    const [imageLoading, setImageLoading] = useState(true);

    // Função para buscar a imagem da CoinGecko usando o mesmo método do Agent
    async function fetchCryptoImage(symbol) {
        try {
            setImageLoading(true);
            // Usa o serviço do Agent para buscar informações da criptomoeda
            const data = await getCriptoInfo(symbol);
            if (data && data.image) {
                setImageUrl(data.image);
            } else {
                // Fallback para CDN se não encontrar
                setImageUrl(getFallbackImageUrl(symbol));
            }
        } catch (error) {
            console.error(`Erro ao buscar imagem para ${symbol}:`, error);
            // Fallback para CDN em caso de erro
            setImageUrl(getFallbackImageUrl(symbol));
        } finally {
            setImageLoading(false);
        }
    }

    // Função de fallback usando CDN (caso a API falhe)
    function getFallbackImageUrl(symbol) {
        const symbolLower = symbol.toLowerCase();
        // Mapeamento básico para fallback
        const symbolMap = {
            'sol': 'sol',
            'btc': 'btc',
            'eth': 'eth',
            'bnb': 'bnb',
            'usdt': 'usdt',
            'usdc': 'usdc',
            'ada': 'ada',
            'dot': 'dot',
            'doge': 'doge',
            'xrp': 'xrp',
            'ltc': 'ltc',
            'link': 'link',
            'avax': 'avax',
            'matic': 'matic',
            'uni': 'uni',
            'atom': 'atom',
            'xlm': 'xlm',
            'trx': 'trx',
            'vet': 'vet',
            'fil': 'fil',
        };
        
        const mappedSymbol = symbolMap[symbolLower] || symbolLower;
        return `https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@bea1a9722a8c63169dcc06e86182bf2c55a76bbc/128/color/${mappedSymbol}.png`;
    }

    function handleImageError(e) {
        // Se falhar, tenta o fallback genérico
        e.target.src = 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@bea1a9722a8c63169dcc06e86182bf2c55a76bbc/128/color/generic.png';
    }

    // Busca a imagem quando o componente monta
    useEffect(() => {
        if (props.data.symbol) {
            fetchCryptoImage(props.data.symbol);
        }
    }, [props.data.symbol]);

    function formatNumber(num, decimals = 8) {
        if (!num && num !== 0) return '-';
        const numValue = parseFloat(num);
        if (isNaN(numValue)) return '-';
        return numValue.toLocaleString('pt-BR', {
            minimumFractionDigits: 0,
            maximumFractionDigits: decimals
        });
    }

    function formatCurrency(value) {
        return formatFiatAmount(value, props.fiat);
    }

    return (
        <div className="wallet-card">
            <div className="wallet-card-header">
                <img 
                    alt={props.data.symbol} 
                    width={48} 
                    height={48}
                    src={imageUrl || getFallbackImageUrl(props.data.symbol)} 
                    onError={handleImageError}
                    className="wallet-card-icon"
                />
                <div className="wallet-card-title">
                    <h3>{props.data.symbol}</h3>
                </div>
            </div>
            
            <div className="wallet-card-grid">
                <div className="wallet-card-item">
                    <h4>
                        <svg className="icon icon-xs me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Disponível
                    </h4>
                    <p>{formatNumber(props.data.available)}</p>
                </div>
                
                <div className="wallet-card-item">
                    <h4>
                        <svg className="icon icon-xs me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                        </svg>
                        Bloqueado
                    </h4>
                    <p>{parseFloat(props.data.onOrder) > 0 ? formatNumber(props.data.onOrder) : '-'}</p>
                </div>
                
                <div className="wallet-card-item">
                    <h4>
                        <svg className="icon icon-xs me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                            <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" />
                        </svg>
                        Total em Fiat
                    </h4>
                    <p className="wallet-card-fiat">{props.data.fiatEstimate ? formatCurrency(props.data.fiatEstimate) : '-'}</p>
                </div>
                
                <div className="wallet-card-item">
                    <h4>
                        <svg className="icon icon-xs me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                        </svg>
                        Preço Médio
                    </h4>
                    <p>{props.data.avg ? formatCurrency(props.data.avg) : '-'}</p>
                </div>
            </div>
        </div>
    );
}

export default WalletCard;

